import {
  type ActiveElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  type ChartEvent,
  Interaction,
  type InteractionModeFunction,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import classNames from "classnames";
import * as React from "react";

import {
  FALLBACK_COLOR,
  resolveHoverColors,
  resolveSeriesColors,
  seriesColorVars,
} from "@/shared/charts";
import { SeriesColor } from "@/shared/domain/dashboards/DashboardDefinition";
import { ChartDisplayType } from "@/shared/domain/metrics";
import { nsToDateLabel } from "@/shared/time/time";

import styles from "./MetricChart.module.css";

// Register only the Chart.js pieces this chart uses (matches the explicit
// registration style in PlotRenderer.ts). Registration is global + idempotent.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

export interface PlotDatum {
  /** Bucket start, Unix-epoch nanoseconds. `bigint` to keep full ns precision:
   *  on the category x-axis Chart.js never reads these (it scales off
   *  `label`), they only ride along for the click → drill-down query. */
  start_time: bigint;
  /** Bucket end, Unix-epoch nanoseconds. See `start_time`. */
  end_time: bigint;
  /** Absent on placeholder rows; present on real aggregate buckets. */
  value?: number;
  /** Pre-formatted x-axis label (short ISO date). Category x-axis only. */
  label: string;
  /** Pre-formatted tooltip title (start–end range). */
  tooltipTitle: string;
  /** Linear x-axis only: this point's x coordinate in Unix-epoch
   *  MILLISECONDS. Chart.js scales numerically off this, so points sit at
   *  their real elapsed distance from one another rather than at even
   *  positional intervals. Ignored on the category axis. */
  x?: number;
  /** Raw mode only: the single session this point came from. */
  session_id?: string;
  /** GROUPED mode only: the value of the card's group-by field for this
   *  point's rows, as the backend cast it to text. `null` is the "field
   *  unset" group — a series of its own, not an absent one. Absent entirely
   *  (rather than null) on an ungrouped chart, whose points belong to no
   *  group; that absence is what makes the view draw ONE dataset labelled by
   *  the metric, exactly as before. Carried through to the drill-down, which
   *  filters on it. */
  group_key?: string | null;
  /** GROUPED mode only: what the legend shows for this point's series (see
   *  `groupSeriesLabel` — `group_key` is a wire value and may be null). */
  group_label?: string;
}

/**
 * How the x-axis maps a point to a position.
 *
 * `category` — evenly spaced positions labelled by {@link PlotDatum.label}.
 * The AGGREGATED path: its buckets are uniform and gap-filled, so even
 * spacing IS time-proportional.
 *
 * `linear` — numeric epoch-ms positions from {@link PlotDatum.x}. The RAW
 * path: one point per session at whatever moment that session ended, with no
 * uniform bucket to synthesize gaps from. Category spacing would draw a
 * three-year-old session and yesterday's an equal distance apart.
 */
export type ChartXScale = "category" | "linear";

/**
 * The span a LINEAR x-axis is pinned to, in epoch MILLISECONDS — the unit
 * {@link PlotDatum.x} carries.
 *
 * Without one Chart.js scales the axis to its data's own min/max, so a chart
 * whose metric stopped reporting early draws a shorter window than its
 * neighbours over the same selection. Ignored on the category axis, whose
 * extent is its (already gap-filled and padded) label list.
 */
export interface ChartXDomain {
  minMs: number;
  maxMs: number;
}

/**
 * The display this axis can actually draw.
 *
 * Bar needs the CATEGORY axis, whose evenly-spaced slots give every bar the
 * same width. The linear axis a raw series draws on is irregular by
 * construction — one point per session, at whatever moment that session ended
 * — and Chart.js sizes bars from the smallest gap between points, so a single
 * close pair shrinks every bar on the chart to a hairline. A card that asks
 * for one there gets the line it would otherwise be, rather than a plot that
 * looks empty.
 *
 * Coerced HERE, at the one place that knows both the requested display and the
 * axis, rather than in each caller: the picker simply does not offer bar for a
 * linear-axis card, so this only ever catches a display that arrived some
 * other way (a hand-edited definition, or one saved while the card was
 * aggregated).
 */
function drawableDisplay(
  display: ChartDisplayType,
  xScale: ChartXScale,
): ChartDisplayType {
  return display === "bar" && xScale === "linear" ? "line" : display;
}

export interface MetricChartViewProps {
  /** Pre-computed series: gap-filled with formatted labels for the category
   *  axis, or sorted ascending by `x` for the linear one. */
  series: PlotDatum[];
  metricName: string;
  /** Unit of measure for the plotted values; titles the y-axis. Omit if unitless. */
  unit?: string | null;
  /** Line (default), bar, or scatter rendering. Bar is drawn only on the
   *  category axis — Chart.js sizes bars from the smallest inter-point
   *  interval, which on an irregular linear axis collapses them to hairlines —
   *  so a bar asked for on the linear axis falls back to a line (see
   *  {@link drawableDisplay}). Line and scatter draw on either. */
  display?: ChartDisplayType;
  /** Positional (default) or time-proportional x-axis. */
  xScale?: ChartXScale;
  /** Palette slot the first series is drawn in; further series follow on from
   *  it. Omit for the automatic assignment. */
  color?: SeriesColor;
  /** Linear axis only: pin the axis to this span instead of letting Chart.js
   *  fit it to the data. See {@link ChartXDomain}. */
  xDomain?: ChartXDomain;
  /**
   * Fired when a real (non-placeholder) datum is clicked.
   *
   * `datum` is the point under the cursor — the series actually aimed at, not
   * the first one. `selection` is everything the chart reported for that
   * click: on the linear axis, each group's nearest point (see
   * {@link NEAREST_PER_GROUP}); elsewhere, the whole hovered index. It is what
   * the tooltip listed, so a caller that drills into `selection` shows exactly
   * the rows the tooltip named. `datum` is always its first entry.
   */
  onDatumClick?: (datum: PlotDatum, selection: PlotDatum[]) => void;
}

/**
 * The dataset's `data` array for `xScale`.
 *
 * Category: bare y values, positioned by their index against `labels`.
 * Linear: `{x, y}` pairs, positioned numerically by epoch-ms `x`. A datum
 * with no value plots as `NaN`, which Chart.js skips — the linear path only
 * ever carries real points, so this is the type-level floor rather than a
 * case the raw series produces.
 */
function datasetData(
  series: PlotDatum[],
  xScale: ChartXScale,
): (number | null)[] | { x: number; y: number }[] {
  return xScale === "linear"
    ? series.map((d) => ({ x: d.x ?? Number.NaN, y: d.value ?? Number.NaN }))
    : series.map((d) => d.value ?? null);
}

/** Category labels, or `undefined` on the linear axis (which reads its
 *  positions off the data instead, and renders its own tick text).
 *
 * Taken from the FIRST partition: every series is gap-filled over the union of
 * buckets (see `toGroupedSeries`), so they all carry the same labels in the
 * same order — which is what lets them share one category axis. */
function chartLabels(
  partitions: SeriesPartition[],
  xScale: ChartXScale,
): string[] | undefined {
  if (xScale === "linear") {
    return undefined;
  }
  return (partitions[0]?.points ?? []).map((d) => d.label);
}

/** Chart.js interaction mode name for {@link nearestPerGroup}. Declared on
 *  Chart.js's own mode map so `interaction.mode` accepts it by name. */
declare module "chart.js" {
  interface InteractionModeMap {
    nearestPerGroup: InteractionModeFunction;
  }
}

const NEAREST_PER_GROUP = "nearestPerGroup";

/**
 * How far from the cursor (px) a group's nearest point may sit and still be
 * reported.
 *
 * Without a cutoff every group always contributes, so a series that stopped
 * reporting in March would answer a hover in September with its last point —
 * a reading the chart does not show anywhere near the cursor. Roughly the
 * width of a point plus its neighbour's, which is the neighbourhood a reader
 * takes a crosshair to be asking about.
 */
const NEAREST_GROUP_RADIUS_PX = 24;

/**
 * Each group's point nearest the cursor's x, at most one per dataset.
 *
 * Chart.js's own `index` mode reports the same ARRAY INDEX from every dataset,
 * which is only the same moment when every series carries the same buckets.
 * The aggregated path guarantees that by gap-filling; the raw path
 * deliberately does not (`toRawSeries`), so there index `i` is a different
 * session at a different time in each group and `index` mode pairs up readings
 * that never coexisted. This asks the question that actually makes sense on a
 * time axis — "what was each group doing HERE" — and answers it with real
 * points rather than synthesized ones.
 */
function nearestPerGroup(
  chart: Chart,
  event: ChartEvent,
  _options: unknown,
  useFinalPosition?: boolean,
): ActiveElement[] {
  const cursorX = event.x;
  if (cursorX === null || cursorX === undefined) {
    return [];
  }
  const hits: { element: ActiveElement; distance: number }[] = [];
  for (const meta of chart.getSortedVisibleDatasetMetas()) {
    let best: { index: number; distance: number } | undefined;
    for (const [index, element] of meta.data.entries()) {
      const { x } = element.getProps(["x"], useFinalPosition);
      const distance = Math.abs(x - cursorX);
      if (best === undefined || distance < best.distance) {
        best = { index, distance };
      }
    }
    if (best !== undefined && best.distance <= NEAREST_GROUP_RADIUS_PX) {
      hits.push({
        element: {
          datasetIndex: meta.index,
          index: best.index,
          element: meta.data[best.index],
        },
        distance: best.distance,
      });
    }
  }
  // Nearest first, so `elements[0]` is the point actually aimed at — which is
  // what makes the caller's "clicked datum" the series under the cursor
  // without asking Chart.js a second question.
  hits.sort((a, b) => a.distance - b.distance);
  return hits.map((hit) => hit.element);
}

Interaction.modes[NEAREST_PER_GROUP] =
  nearestPerGroup as (typeof Interaction.modes)["nearest"];

/**
 * The groups the reader has switched off, by {@link SeriesPartition.key}.
 *
 * Keyed by GROUP, not by index. Series are ordered by group key
 * (`toGroupedSeries`), so a group appearing in a later refetch shifts every
 * index after it — an index-keyed set would silently move the reader's choice
 * onto a different device. A key no longer present simply matches nothing,
 * which is what makes a hidden series survive a regrouping and come back with
 * its group.
 */
type HiddenSeriesKeys = ReadonlySet<string | null | undefined>;

/** One plotted line: the group it stands for and its points. */
interface SeriesPartition {
  /** The group's wire value, `null` for the "no value" group, and
   *  `undefined` on an UNGROUPED chart — whose one line belongs to no group
   *  and is labelled by the metric instead. */
  key: string | null | undefined;
  /** Legend text, or `undefined` on an ungrouped chart. */
  label: string | undefined;
  points: PlotDatum[];
}

/**
 * Split a flat series into one partition per `group_key`, in first-appearance
 * order.
 *
 * A series whose points carry no `group_key` at all — every ungrouped chart,
 * and every raw one — yields exactly ONE partition, which is what keeps those
 * paths drawing the single unlabelled line they always drew.
 */
function partitionSeries(series: PlotDatum[]): SeriesPartition[] {
  const byKey = new Map<string | null | undefined, SeriesPartition>();
  for (const datum of series) {
    const key = datum.group_key;
    let partition = byKey.get(key);
    if (partition === undefined) {
      partition = { key, label: datum.group_label, points: [] };
      byKey.set(key, partition);
    }
    partition.points.push(datum);
  }
  return [...byKey.values()];
}

/**
 * One Chart.js dataset per partition, labelled and coloured — at rest and
 * hovered.
 *
 * The hover colours are stated rather than left to Chart.js, which derives its
 * own from `backgroundColor` but cannot parse the colour space the series
 * palette computes to; see {@link resolveHoverColors}. Stating them keeps one
 * highlight treatment across every series and both chart types, so a grouped
 * chart answers the pointer the way an ungrouped one always has.
 */
function buildDatasets(
  partitions: SeriesPartition[],
  xScale: ChartXScale,
  metricName: string,
  canvas: HTMLCanvasElement,
  hiddenKeys: HiddenSeriesKeys,
  display: ChartDisplayType,
  colorOverride?: SeriesColor,
) {
  const colors = resolveSeriesColors(canvas, partitions.length, colorOverride);
  const hoverColors = resolveHoverColors(canvas, colors);
  return partitions.map((partition, i) => ({
    label: partition.label ?? metricName,
    // The build-time half of the visibility story; the update effect asserts
    // it again through `setDatasetVisibility`, which is what Chart.js treats
    // as authoritative once a dataset's metadata exists.
    hidden: hiddenKeys.has(partition.key),
    data: datasetData(partition.points, xScale),
    borderColor: colors[i] ?? FALLBACK_COLOR,
    backgroundColor: colors[i] ?? FALLBACK_COLOR,
    hoverBackgroundColor: hoverColors[i] ?? FALLBACK_COLOR,
    hoverBorderColor: hoverColors[i] ?? FALLBACK_COLOR,
    // Connect across missing buckets (rendered as null) instead of leaving
    // gaps; the gap-fill still keeps the x-axis spacing time-proportional.
    spanGaps: true,
    // A scatter IS the line controller with the connecting line switched off,
    // rather than Chart.js's own `scatter` type: that controller insists on
    // `{x, y}` data and a numeric x-axis, which the category path does not
    // have. Reusing the line controller is what lets one display work on both
    // axes, and keeps the points, hover colours and interaction modes
    // identical to the line chart's. Ignored by the bar controller.
    showLine: display !== "scatter",
  }));
}

/**
 * Chart presentation layer for metrics.
 *
 * Renders a Chart.js canvas from an already-computed {@link PlotDatum} series,
 * or an empty-state message when the series is empty.
 *
 * Does no data fetching or transformation — that belongs in the {@link MetricChart} wrapper.
 */
export function MetricChartView({
  series,
  metricName,
  unit = null,
  display = "line",
  xScale = "category",
  xDomain,
  color: seriesColor,
  onDatumClick,
}: MetricChartViewProps) {
  const chartRef = React.useRef<Chart | null>(null);

  // The chart is created once per `display` type and then mutated in place (see
  // the update effect below) as the series / metric name change, so it — and
  // its option callbacks — outlive those data changes. The callbacks therefore
  // read the latest values through refs instead of closing over stale ones.
  const clickRef = React.useRef(onDatumClick);
  clickRef.current = onDatumClick;
  // One entry per plotted line. Recomputed on every render (it is a linear
  // pass over the series the parent already memoized) and held in a ref for
  // the same reason the other option-callback inputs are: the chart outlives
  // the data.
  const partitions = partitionSeries(series);
  const partitionsRef = React.useRef(partitions);
  partitionsRef.current = partitions;
  const metricNameRef = React.useRef(metricName);
  metricNameRef.current = metricName;
  const unitRef = React.useRef(unit);
  unitRef.current = unit;
  // Read at build time and reassigned by the update effect below, for the same
  // reason the other option inputs are held in refs: the chart outlives the
  // data, and `setCanvas` must not be rebuilt when only the domain moves.
  const xDomainRef = React.useRef(xDomain);
  xDomainRef.current = xDomain;

  // Which series the reader has switched off through the legend. Local to the
  // view: it is a way of looking at this chart, not a property of the card's
  // configuration, so it is deliberately not persisted or lifted — and it
  // outlives a refetch, which is the point (see {@link HiddenSeriesKeys}).
  const [hiddenKeys, setHiddenKeys] = React.useState<HiddenSeriesKeys>(
    () => new Set(),
  );
  const hiddenKeysRef = React.useRef(hiddenKeys);
  hiddenKeysRef.current = hiddenKeys;

  const toggleSeries = React.useCallback((key: string | null | undefined) => {
    setHiddenKeys((current) => {
      const next = new Set(current);
      // `delete` reports whether the key was there, so one call both tests
      // and un-hides.
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Build (and tear down) the chart as the <canvas> mounts and whenever
  // `display` or `xScale` changes — Chart.js can't switch an existing chart's
  // type or a scale's type, so those are the cases that need a fresh instance.
  // (A line ⇄ scatter switch keeps the same controller and would survive an
  // update alone, but it rides along here rather than being special-cased.)
  // Series / metric name changes never reach here; they flow through
  // chart.update() below. The canvas is absent in the empty state, so this
  // also disposes the chart when the series empties out.
  const setCanvas = React.useCallback(
    (canvas: HTMLCanvasElement | null) => {
      chartRef.current?.destroy();
      chartRef.current = null;
      if (!canvas) {
        return;
      }

      const drawn = drawableDisplay(display, xScale);
      chartRef.current = new Chart(canvas, {
        type: drawn === "bar" ? "bar" : "line",
        data: {
          labels: chartLabels(partitionsRef.current, xScale),
          datasets: buildDatasets(
            partitionsRef.current,
            xScale,
            metricNameRef.current,
            canvas,
            hiddenKeysRef.current,
            drawn,
            seriesColor,
          ),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          // Hovering marks the whole BUCKET, every series in it — deliberately,
          // not as a side effect of `index` being the tooltip's mode. The
          // tooltip lists every series' value at that bucket and the click
          // drills into the bucket itself (MetricChart forwards only its
          // bounds), so the highlight names exactly what the pointer is aimed
          // at. It is the single-series rule — the bar at this index lights up
          // — applied to however many series are drawn.
          // The linear axis carries series that do NOT share an index (see
          // `nearestPerGroup`); the category axis does, and `index` is both
          // correct and cheaper there.
          interaction:
            xScale === "linear"
              ? { mode: NEAREST_PER_GROUP, intersect: false }
              : { mode: "index", intersect: false },
          onClick: (
            event: ChartEvent,
            elements: ActiveElement[],
            chart: Chart,
          ) => {
            const hit = clickedElement(event, elements, chart);
            if (hit === undefined) {
              return;
            }
            const datum =
              partitionsRef.current[hit.datasetIndex]?.points[hit.index];
            // A gap-filled placeholder carries no value and stands for no
            // rows, so it is not something to drill into.
            if (datum && datum.value !== undefined) {
              // Everything the chart reported for this click, clicked point
              // first — the same set the tooltip listed, so a caller can drill
              // into exactly the rows the reader was looking at.
              const selection = elements
                .map(
                  (el) =>
                    partitionsRef.current[el.datasetIndex]?.points[el.index],
                )
                .filter(
                  (point): point is PlotDatum =>
                    point !== undefined && point.value !== undefined,
                );
              const ordered = [
                datum,
                ...selection.filter((point) => point !== datum),
              ];
              clickRef.current?.(datum, ordered);
            }
          },
          scales: {
            x:
              xScale === "linear"
                ? {
                    type: "linear",
                    // `undefined` is Chart.js's own "fit to the data", which is
                    // what a chart drawn outside a dashboard still gets.
                    min: xDomainRef.current?.minMs,
                    max: xDomainRef.current?.maxMs,
                    ticks: {
                      autoSkip: true,
                      maxRotation: 0,
                      // Linear ticks land on Chart.js "nice numbers" in epoch
                      // ms rather than on calendar boundaries, so each is
                      // rendered as the date it falls on. (Every dashboard
                      // range is at least 30 days, so date resolution is the
                      // right granularity.) The ×1e6 is ms → ns for the shared
                      // formatter.
                      callback: (value: string | number) =>
                        nsToDateLabel(Number(value) * 1_000_000),
                    },
                  }
                : { ticks: { autoSkip: true, maxRotation: 0 } },
            y: {
              beginAtZero: true,
              title: {
                display: Boolean(unitRef.current),
                text: unitRef.current ?? "",
              },
            },
          },
          plugins: {
            // Chart.js's own legend stays off on EVERY path: a canvas legend
            // is laid out inside the plot and cannot scroll, so many series
            // would leave a legend and no chart. The view renders a scrollable
            // HTML one below instead, and only when there is more than one
            // series to tell apart.
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const first = items[0];
                  if (first === undefined) {
                    return "";
                  }
                  // Read off the item's OWN partition, not partition 0. On the
                  // category axis the two agree — every partition is gap-filled
                  // over the same buckets — but on the linear axis they do not:
                  // each group's points are its own sessions at its own
                  // moments, so partition 0's `index`-th point names a
                  // different instant, and past its length names none at all.
                  return (
                    partitionsRef.current[first.datasetIndex]?.points[
                      first.dataIndex
                    ]?.tooltipTitle ?? ""
                  );
                },
                label: (item) => {
                  const y = item.parsed.y;
                  const value =
                    typeof y !== "number" || Number.isNaN(y)
                      ? "—"
                      : `${String(y)}${unitRef.current ? ` ${unitRef.current}` : ""}`;
                  // With one line the dataset label is the metric name, which
                  // the card's title already says. With several it is the
                  // group, which is the only thing distinguishing the rows.
                  return partitionsRef.current.length > 1
                    ? `${item.dataset.label ?? ""}: ${value}`
                    : value;
                },
              },
            },
          },
        },
      });
    },
    [display, xScale, seriesColor],
  );

  // Push series / metric-name / unit changes into the existing chart rather than
  // rebuilding it (Chart.js's recommended path for data updates). The tooltip
  // callback reads the unit off its ref, but the y-axis title is baked into the
  // options object at build time, so it has to be reassigned here.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    chart.data.labels = chartLabels(partitions, xScale);
    // Rebuilt wholesale rather than patched in place: grouping changes the
    // NUMBER of datasets, not just their contents, and a stale extra dataset
    // would keep drawing a series the response no longer has.
    chart.data.datasets = buildDatasets(
      partitions,
      xScale,
      metricName,
      chart.canvas,
      hiddenKeys,
      drawableDisplay(display, xScale),
      seriesColor,
    );
    // Chart.js keeps visibility on the dataset's METADATA, which survives the
    // wholesale dataset replacement above — so a dataset rebuilt as visible
    // would still be drawn hidden (and vice versa) from whatever the metadata
    // at that index last held. Restating it here is what makes the legend and
    // the plot agree after a refetch or a regrouping.
    partitions.forEach((partition, i) => {
      chart.setDatasetVisibility(i, !hiddenKeys.has(partition.key));
    });
    // `scales.y` is typed as a union over every scale kind (linear, time,
    // radial…), so `title` isn't reachable without narrowing to the cartesian
    // shape we actually build above.
    const yScale = chart.options.scales?.y as
      | { title?: { display?: boolean; text?: string } }
      | undefined;
    if (yScale?.title) {
      yScale.title.display = Boolean(unit);
      yScale.title.text = unit ?? "";
    }
    // Baked into the options object at build time like the y-axis title, so a
    // domain that moves — the dashboard's range changed, but neither `display`
    // nor `xScale` did — has to be reassigned here or the axis keeps the span
    // it was created with. Narrowed the same way, and only for the linear
    // axis: the category scale has no numeric bounds to set.
    if (xScale === "linear") {
      const xScaleOptions = chart.options.scales?.x as
        | { min?: number; max?: number }
        | undefined;
      if (xScaleOptions) {
        xScaleOptions.min = xDomain?.minMs;
        xScaleOptions.max = xDomain?.maxMs;
      }
    }
    chart.update();
    // `partitions` is derived from `series` on every render, so `series` is
    // the honest dependency — depending on the derived array would re-run this
    // effect on every render instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    series,
    metricName,
    display,
    xScale,
    unit,
    xDomain?.minMs,
    xDomain?.maxMs,
    hiddenKeys,
  ]);

  if (series.length === 0) {
    return (
      <p className={styles.empty}>No data points in the selected window.</p>
    );
  }

  const legendVars = seriesColorVars(partitions.length, seriesColor);

  return (
    <div className={styles.chart}>
      {partitions.length > 1 && (
        <ul className={styles.legend} aria-label={`${metricName} series`}>
          {partitions.map((partition, i) => {
            const hidden = hiddenKeys.has(partition.key);
            return (
              <li
                key={partition.key ?? "__no_value__"}
                className={styles.legendItem}
              >
                {/* A toggle button rather than a plain row: this is the only
                 * control that switches a series off, so it has to be
                 * reachable by keyboard and to say which state it is in.
                 * `aria-pressed` carries that — the swatch is decorative, and
                 * a colour tells a screen reader nothing. */}
                <button
                  type="button"
                  className={classNames(styles.legendToggle, {
                    [styles.isHidden]: hidden,
                  })}
                  aria-pressed={!hidden}
                  onClick={() => {
                    toggleSeries(partition.key);
                  }}
                >
                  <span
                    className={styles.legendSwatch}
                    // Per-series, so inline; still the SAME token the canvas
                    // reads for this series' line.
                    style={{ backgroundColor: `var(${legendVars[i]})` }}
                    aria-hidden="true"
                  />
                  <span className={styles.legendLabel}>
                    {partition.label ?? metricName}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div
        className={classNames(styles.container, {
          [styles.clickable]: Boolean(onDatumClick),
        })}
      >
        <canvas ref={setCanvas} role="img" aria-label={`${metricName} chart`} />
      </div>
    </div>
  );
}

/**
 * Which (dataset, point) the user actually clicked.
 *
 * The chart's interaction mode is `index`, so `elements` holds one entry per
 * dataset at the hovered index — and `elements[0]` is always dataset 0, which
 * on a grouped chart would drill into the first series wherever the user
 * clicked. With several series the click is re-resolved in `nearest` mode,
 * which returns the single closest element and therefore the series actually
 * aimed at. With one series there is nothing to disambiguate, so the cheaper
 * (and long-standing) path is kept.
 */
/**
 * The plotted point the click landed on.
 *
 * `elements` is not it: the chart's `interaction.mode` is `"index"`, so
 * Chart.js reports the whole index and hands back dataset 0 first whatever was
 * clicked — which would report the first line's datum for a click on the
 * second. Asking again in `"nearest"` mode finds the series actually under the
 * cursor.
 */
function clickedElement(
  event: ChartEvent,
  elements: ActiveElement[],
  chart: Chart,
): ActiveElement | undefined {
  if (chart.data.datasets.length <= 1 || !event.native) {
    return elements[0];
  }
  return chart.getElementsAtEventForMode(
    event.native,
    "nearest",
    { intersect: false },
    false,
  )[0];
}
